import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga';
import moment from 'moment';

import { useAuth } from '../../../authentication/AuthenticationContext';
import { GameAccount } from '../../../authentication/Authentication.model';
import { Modal } from '../../../_shared/modal/Modal';
import { useConfig } from '../../../ConfigContext';
import { RequestError } from '../../../Error.model';
import { myI18n } from '../../../i18n';

interface Props {
  providerName: string;
  logo: string;
  error?: RequestError;
  link: ()=>void;
  unlink?: ()=>void;
  showLink?: boolean;
  showUnlink?: boolean;
}

export function ProviderLink({
  providerName,
  logo,
  error,
  link,
  unlink,
  showLink = true,
  showUnlink = true,
}: Props): JSX.Element {
  const auth = useAuth();
  const config = useConfig();
  const { t } = useTranslation(['translation', 'error']);
  const [providerAccount, setProviderAccount] = useState<GameAccount>();
  const [showConfirmModalProvider, setShowConfirmModalProvider] = useState(false);
  const [showCustomErrorModal, setShowCustomErrorModal] = useState(false);
  const [showModalDisconnect, setShowModalDisconnect] = useState(false);

  const getProviderAccount = (): void => {
    const pa = auth.user.accounts.find((account) => account.type === providerName);
    setProviderAccount(pa);
  };

  const formatDate = (date: string): string => new Date(date).toLocaleDateString();

  const convertSeconds = (seconds: number): string => {
    const now = moment();
    const expiredAt = moment().add(seconds, 'seconds');

    return `${expiredAt.diff(now, 'days')}`;
  };

  const openLinkModal = (): void => {
    setShowConfirmModalProvider(true);
  };

  const openDisconnectModal = ():void => {
    setShowModalDisconnect(true);
  };

  const closeLinkModal = (): void => {
    setShowConfirmModalProvider(false);

    ReactGA.event(
      { category: 'User',
        action: `confirmation-linking-cancel-${providerName}` },
    );
  };

  const confirmLinkModal = ():void => {
    setShowConfirmModalProvider(false);
    link();

    ReactGA.event(
      { category: 'User',
        action: `confirmation-linking-confirm-${providerName}` },
    );
  };

  const confirmDisconnectModal = ():void => {
    setShowModalDisconnect(false);
    unlink();
  };

  const closeDisconnectModal = ():void => {
    setShowModalDisconnect(false);
  };

  const closeCustomErrorModal = ():void => {
    setShowCustomErrorModal(false);
  };

  const modalErrorRendering = (): JSX.Element => {
    if (error && myI18n.exists(`error:${error.code}`)) {
      return (
        <p>
          <span>
            {t(
              'translation:account.provider.modal.error.description1',
              { error: t(`error:${error.code}`, {
                provider: error?.data?.provider,
                date: formatDate(error?.data?.date as string),
              }) },
            )}
          </span>
          <br />
          <br />
          <span>
            {t('translation:account.provider.modal.error.description2')}
          </span>
        </p>
      );
    }

    return <p>{t('translation:account.provider.modal.error.descriptionGeneric')}</p>;
  };

  useEffect(() => {
    if (error) {
      setShowCustomErrorModal(true);
    }
  }, [error]);

  useEffect(() => {
    getProviderAccount();
  }, [auth.user.accounts.length]);

  return (
    <div className='provider-link-container text-center'>
      <img src={logo} alt={providerName} height='48' />
      <h4 className='mb-3'>{t(`translation:${providerName}`)}</h4>
      {
        providerAccount && (
          <>
            <div className='provider-username'>{providerAccount ? providerAccount.userName : <br />}</div>
            <div className='added-date mt-1 mb-3'>
              {providerAccount ? `Added in ${formatDate(providerAccount.createdAt)}` : <br />}
            </div>
          </>
        )
      }

      <div className='link mx-auto' data-cy={`${providerName}-link-status-label`}>
        {providerAccount
          ? t('translation:account.my-account.linked')
          : t('translation:account.my-account.unlinked')}
        <div className={`status ${providerAccount ? 'linked' : 'unlinked'}`} />
      </div>

      {!providerAccount && showLink
      && (
        <button
          type='button'
          onClick={openLinkModal}
          disabled={providerName === 'stadia'}
          className='btn btn-primary-game btn-block mx-auto mt-3 mb-0'
          data-cy={`${providerName}-link-button`}
        >
          {t('translation:account.my-account.link')}
        </button>
      )}
      {providerAccount && showUnlink
      && (
        <button
          type='button'
          disabled={providerName === 'stadia'}
          onClick={openDisconnectModal}
          className='btn btn-secondary btn-block mx-auto mt-3 mb-0'
          data-cy={`${providerName}-unlink-button`}
        >
          {t('translation:account.my-account.unlink')}
        </button>
      )}

      <Modal
        show={showConfirmModalProvider}
        confirm={confirmLinkModal}
        close={closeLinkModal}
        textConfirm={t('translation:button.confirm')}
        textClose={t('translation:common.cancel')}
      >
        {t('translation:account.provider.confirm-provider', {
          limit: convertSeconds((config.serverConfig) ? config.serverConfig.gameAccount.restrictionSecondsLinking : 0),
          provider: t(`translation:${providerName}`),
        })}
      </Modal>

      <Modal
        className='unknown-error'
        show={showCustomErrorModal}
        confirm={closeCustomErrorModal}
        textConfirm={t('translation:button.close')}
        classConfirm='btn btn-primary-game'
      >
        <h2 className='text-uppercase'>{t('translation:account.provider.modal.error.title')}</h2>
        {modalErrorRendering()}
      </Modal>

      <Modal
        show={showModalDisconnect}
        confirm={confirmDisconnectModal}
        close={closeDisconnectModal}
        textConfirm={t('translation:button.confirm')}
        textClose={t('translation:common.cancel')}
      >
        <p>
          {t('translation:account.provider.confirm-disconnect1', {
            limit: convertSeconds((config.serverConfig)
              ? config.serverConfig.gameAccount.restrictionSecondsLinking : 0),
            provider: t(`translation:${providerName}`),
          })}
          <br />
          <span className='font-weight-bold'>
            {t('translation:account.provider.confirm-disconnect2')}
          </span>
        </p>
      </Modal>
    </div>
  );
}
